import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import {
  Dialog,
  DialogContent
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MessageSquare, History, AlertCircle, Building2, Truck, Home, MapPin, Upload, Image as ImageIcon, Trash2, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import pqrsService from '@/services/pqrs.service';
import { uploadDocumentFlow, getPresignedDownload, getDownloadUrlOrOpen } from '@/services/documentsService';

const PqrManageModal = ({ isOpen, onClose, pqr, onUpdate }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [nuevoEstado, setNuevoEstado] = useState('');
  const [comentario, setComentario] = useState('');
  const [accion, setAccion] = useState('');
  const [solucion, setSolucion] = useState('');
  const [selectedImages, setSelectedImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [imageUrls, setImageUrls] = useState({});
  const [commentImageUrls, setCommentImageUrls] = useState({});

  useEffect(() => {
    if (pqr) {
      setNuevoEstado(pqr.estado || 'Abierta');
      setComentario('');
      setAccion('');
      setSelectedImages([]);
      loadImageUrls();
      loadCommentImageUrls();
    }
  }, [pqr]);

  const loadImageUrls = async () => {
    if (!pqr?.adjuntos || pqr.adjuntos.length === 0) return;
    const urls = {};
    for (const adj of pqr.adjuntos) {
      try {
        if (adj.id) {
          const url = await getDownloadUrlOrOpen({ id: adj.id });
          urls[adj.id] = url;
        }
      } catch (error) {
        console.error('Error loading image:', error);
      }
    }
    setImageUrls(urls);
  };

  const loadCommentImageUrls = async () => {
    if (!pqr?.comentarios) return;
    const urls = {};
    for (const com of pqr.comentarios) {
      if (com.adjuntos && com.adjuntos.length > 0) {
        for (const adj of com.adjuntos) {
          try {
            if (adj.id) {
              const url = await getDownloadUrlOrOpen({ id: adj.id });
              urls[adj.id] = url;
            }
          } catch (error) {
            console.error('Error loading comment image:', error);
          }
        }
      }
    }
    setCommentImageUrls(urls);
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    setSelectedImages(prev => [...prev, ...imageFiles]);
  };

  const handleRemoveImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleOpenImage = async (adjunto) => {
    try {
      if (adjunto.url) {
        window.open(adjunto.url, '_blank', 'noopener,noreferrer');
      } else if (adjunto.id) {
        const url = await getPresignedDownload({ id: adjunto.id });
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('Error al abrir imagen:', error);
    }
  };

  const handleSubmit = async () => {
    // Validar si está cerrando el caso
    if (nuevoEstado === 'Cerrada' && pqr.estado !== 'Cerrada') {
      if (!solucion.trim()) {
        toast({
          title: "Error",
          description: "Debe ingresar la solución aplicada para cerrar el caso",
          variant: "destructive",
        });
        return;
      }
    }

    if (!comentario.trim() && nuevoEstado === pqr.estado && !solucion.trim() && selectedImages.length === 0) {
      toast({
        title: "Advertencia",
        description: "Debe agregar un comentario, cambiar el estado o ingresar una solución",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // Upload images if any
      let adjuntos = [];
      if (selectedImages.length > 0) {
        setUploadingImages(true);
        try {
          const uploadPromises = selectedImages.map(file =>
            uploadDocumentFlow({
              ownerType: 'pqr',
              ownerId: pqr._id,
              docType: 'imagen',
              file,
            })
          );
          const uploadResults = await Promise.all(uploadPromises);
          adjuntos = uploadResults.map(result => ({
            id: result.id || result._id,
            nombre: result.fileName || result.meta?.fileName,
            url: result.url || result.publicUrl,
          }));
        } catch (uploadError) {
          console.error('Error uploading images:', uploadError);
          toast({
            title: 'Advertencia',
            description: 'Algunas imágenes no se pudieron subir',
            variant: 'destructive',
          });
        } finally {
          setUploadingImages(false);
        }
      }

      // Si está cerrando el caso, usar endpoint específico
      if (nuevoEstado === 'Cerrada' && pqr.estado !== 'Cerrada') {
        const cerrarData = {
          solucion: solucion.trim(),
          usuarioCierre: {
            id: user.id,
            nombre: user.name,
            correo: user.email
          }
        };

        const response = await pqrsService.cerrarPqr(pqr._id, cerrarData);

        if (response.success) {
          toast({
            title: "Éxito",
            description: "PQR cerrada exitosamente",
          });
          setSolucion('');
          setComentario('');
          setAccion('');
          setSelectedImages([]);
          onUpdate();
          onClose();
        }
      } else {
        // Actualización normal
        const updateData = {};

        // Si cambió el estado (pero no a cerrada)
        if (nuevoEstado !== pqr.estado) {
          updateData.estado = nuevoEstado;
        }

        // Si hay comentario
        if (comentario.trim()) {
          updateData.comentario = {
            comentario: comentario.trim(),
            accion: accion || undefined,
            adjuntos: adjuntos.length > 0 ? adjuntos : undefined
          };
        } else if (adjuntos.length > 0) {
          updateData.comentario = {
            comentario: 'Imagen adjunta',
            adjuntos: adjuntos
          };
        }

        // Agregar usuario que actualiza
        updateData.usuarioActualizacion = {
          id: user.id,
          nombre: user.name,
          correo: user.email
        };

        const response = await pqrsService.updatePqr(pqr._id, updateData);

        if (response.success) {
          toast({
            title: "Éxito",
            description: "PQR actualizada exitosamente",
          });
          setComentario('');
          setAccion('');
          setSelectedImages([]);
          onUpdate();
          onClose();
        }
      }
    } catch (error) {
      console.error('Error al actualizar PQR:', error);
      toast({
        title: "Error",
        description: error.message || 'Error al actualizar la PQR',
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getEstadoColor = (estado) => {
    const colors = {
      'Abierta': 'bg-red-100 text-red-800',
      'En proceso': 'bg-blue-100 text-blue-800',
      'En espera': 'bg-yellow-100 text-yellow-800',
      'Cerrada': 'bg-green-100 text-green-800'
    };
    return colors[estado] || 'bg-gray-100 text-gray-800';
  };

  const getPrioridadColor = (prioridad) => {
    const colors = {
      'Alta': 'bg-red-100 text-red-800',
      'Media': 'bg-yellow-100 text-yellow-800',
      'Baja': 'bg-green-100 text-green-800'
    };
    return colors[prioridad] || 'bg-gray-100 text-gray-800';
  };

  if (!pqr) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[92vh] p-0 gap-0">
        {/* Header compacto */}
        <div className="flex items-center justify-between px-5 py-3 pr-12 border-b bg-muted/30">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-orange-500" />
            <div>
              <h2 className="font-semibold text-base leading-tight">Gestionar PQR - {pqr.consecutivo}</h2>
              <div className="flex items-center gap-2 mt-0.5">
                <Badge className={`${getEstadoColor(pqr.estado)} text-xs`}>{pqr.estado}</Badge>
                <Badge className={`${getPrioridadColor(pqr.prioridad)} text-xs`}>{pqr.prioridad}</Badge>
                <span className="text-xs text-muted-foreground">{pqr.tipo} • {pqr.categoria}</span>
              </div>
            </div>
          </div>
          <span className="text-xs text-muted-foreground">
            {format(new Date(pqr.fechaRegistro), 'dd/MM/yyyy HH:mm', { locale: es })}
          </span>
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-5 divide-x max-h-[calc(92vh-120px)]">

          {/* LEFT PANEL — Info + Actions (2 cols) */}
          <ScrollArea className="col-span-2 p-4 max-h-[calc(92vh-120px)]">
            <div className="space-y-3 pr-2">
              {/* Descripción */}
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Descripción</p>
                <p className="text-sm leading-relaxed">{pqr.descripcion}</p>
              </div>

              {/* Reportante */}
              <div className="p-2.5 bg-blue-50/60 border border-blue-100 rounded-lg">
                <p className="text-xs font-medium text-blue-700 mb-1">Reportado por</p>
                <p className="text-sm font-medium">{pqr.usuarioReporta?.nombre}</p>
                {pqr.usuarioReporta?.correo && (
                  <p className="text-xs text-blue-600">{pqr.usuarioReporta.correo}</p>
                )}
                {pqr.usuarioReporta?.telefono && (
                  <p className="text-xs text-blue-600">{pqr.usuarioReporta.telefono}</p>
                )}
              </div>

              {/* Origen */}
              {pqr.origen && (
                <div className="p-2.5 bg-purple-50/60 border border-purple-100 rounded-lg">
                  <div className="flex items-center gap-1.5 mb-1">
                    {pqr.origen.tipo === 'Lavandería' && <Building2 className="h-3.5 w-3.5 text-purple-600" />}
                    {pqr.origen.tipo === 'Ruta / Conductor' && <Truck className="h-3.5 w-3.5 text-purple-600" />}
                    {pqr.origen.tipo === 'Apartamento' && <Home className="h-3.5 w-3.5 text-purple-600" />}
                    <p className="text-xs font-medium text-purple-700">Origen: {pqr.origen.tipo}</p>
                  </div>
                  {pqr.origen.propiedad && (
                    <div className="text-xs text-purple-700 space-y-0.5">
                      <p>{pqr.origen.propiedad.nombre}
                        {pqr.origen.propiedad.edificio && ` • ${pqr.origen.propiedad.edificio.nombre}`}
                        {pqr.origen.propiedad.edificio?.ciudad && ` • ${pqr.origen.propiedad.edificio.ciudad}`}
                      </p>
                      {pqr.origen.apartamento && (
                        <p className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" /> Apto: {pqr.origen.apartamento}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Adjuntos del PQR */}
              {pqr.adjuntos && pqr.adjuntos.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">Adjuntos ({pqr.adjuntos.length})</p>
                  <div className="grid grid-cols-2 gap-2">
                    {pqr.adjuntos.map((adj, idx) => (
                      <div key={idx} className="relative group">
                        {imageUrls[adj.id] ? (
                          <img
                            src={imageUrls[adj.id]}
                            alt={adj.nombre || 'Adjunto'}
                            className="w-full h-24 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity border"
                            onClick={() => window.open(imageUrls[adj.id], '_blank')}
                          />
                        ) : (
                          <div className="w-full h-24 bg-muted rounded-lg flex items-center justify-center border">
                            <ImageIcon className="h-6 w-6 text-muted-foreground animate-pulse" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Separator />

              {/* Cambiar estado */}
              <div className="space-y-1.5">
                <Label htmlFor="estado" className="text-xs font-medium">Cambiar Estado</Label>
                <Select value={nuevoEstado} onValueChange={setNuevoEstado}>
                  <SelectTrigger id="estado" className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Abierta">Abierta</SelectItem>
                    <SelectItem value="En proceso">En proceso</SelectItem>
                    <SelectItem value="En espera">En espera</SelectItem>
                    <SelectItem value="Cerrada">Cerrada</SelectItem>
                  </SelectContent>
                </Select>
                {nuevoEstado !== pqr.estado && (
                  <p className="text-xs text-blue-600">
                    De "{pqr.estado}" → "{nuevoEstado}"
                  </p>
                )}
              </div>

              {/* Solución (solo si se está cerrando) */}
              {nuevoEstado === 'Cerrada' && pqr.estado !== 'Cerrada' && (
                <div className="space-y-1.5 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <Label htmlFor="solucion" className="text-xs font-medium text-yellow-900">
                    Solución Aplicada <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="solucion"
                    value={solucion}
                    onChange={(e) => setSolucion(e.target.value)}
                    placeholder="Describa la solución aplicada..."
                    rows={3}
                    className="border-yellow-300 text-sm"
                  />
                </div>
              )}

              {/* Historial de cambios */}
              {pqr.historialCambios && pqr.historialCambios.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 mb-2">
                    <History className="h-3.5 w-3.5 text-muted-foreground" />
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Historial de Cambios</p>
                  </div>
                  <div className="space-y-1.5 max-h-32 overflow-y-auto">
                    {pqr.historialCambios.map((cambio, index) => (
                      <div key={index} className="p-2 bg-muted/20 rounded text-xs">
                        <div className="flex justify-between items-start">
                          <span className="font-medium">{cambio.usuario?.nombre || 'Usuario'}</span>
                          <span className="text-muted-foreground">
                            {format(new Date(cambio.fecha), 'dd/MM/yy HH:mm', { locale: es })}
                          </span>
                        </div>
                        <p className="text-muted-foreground mt-0.5">
                          <Badge className="text-[10px] px-1 py-0">{cambio.estadoAnterior}</Badge>
                          {' → '}
                          <Badge className="text-[10px] px-1 py-0">{cambio.estadoNuevo}</Badge>
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* RIGHT PANEL — Comments (3 cols) */}
          <div className="col-span-3 flex flex-col max-h-[calc(92vh-120px)]">
            {/* Comment history */}
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="flex items-center gap-2 px-4 py-2.5 border-b bg-blue-50/50">
                <MessageSquare className="h-4 w-4 text-blue-600" />
                <h3 className="font-semibold text-sm text-blue-900">
                  Comentarios
                  {pqr.comentarios?.length > 0 && (
                    <span className="ml-1.5 text-xs font-normal text-blue-600">({pqr.comentarios.length})</span>
                  )}
                </h3>
              </div>

              <ScrollArea className="flex-1 p-4">
                {(!pqr.comentarios || pqr.comentarios.length === 0) ? (
                  <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
                    <MessageSquare className="h-8 w-8 mb-2 opacity-30" />
                    <p className="text-sm">No hay comentarios aún</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pqr.comentarios.map((com, index) => (
                      <div key={index} className="p-3 bg-white border border-gray-100 rounded-lg shadow-sm text-sm">
                        <div className="flex justify-between items-start mb-1.5">
                          <span className="font-semibold text-gray-900">{com.usuario?.nombre || 'Usuario'}</span>
                          <span className="text-[11px] text-muted-foreground">
                            {format(new Date(com.fecha), 'dd/MM/yyyy HH:mm', { locale: es })}
                          </span>
                        </div>
                        <p className="text-gray-700 leading-relaxed">{com.comentario}</p>
                        {com.accion && (
                          <div className="mt-1.5 flex items-center gap-1">
                            <Badge variant="outline" className="text-[11px] text-blue-700 border-blue-200 bg-blue-50">
                              Acción: {com.accion}
                            </Badge>
                          </div>
                        )}
                        {com.adjuntos && com.adjuntos.length > 0 && (
                          <div className="grid grid-cols-2 gap-2 mt-2 pt-2 border-t border-gray-100">
                            {com.adjuntos.map((adj, adjIdx) => (
                              <div key={adjIdx} className="relative group">
                                {commentImageUrls[adj.id] ? (
                                  <img
                                    src={commentImageUrls[adj.id]}
                                    alt={adj.nombre || 'Adjunto'}
                                    className="w-full h-24 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity border"
                                    onClick={() => window.open(commentImageUrls[adj.id], '_blank')}
                                  />
                                ) : (
                                  <div className="w-full h-24 bg-muted rounded flex items-center justify-center border">
                                    <ImageIcon className="h-6 w-6 text-muted-foreground animate-pulse" />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>

            {/* New comment input area */}
            <div className="border-t bg-gray-50/80 p-4 space-y-2.5">
              <Textarea
                id="comentario"
                value={comentario}
                onChange={(e) => setComentario(e.target.value)}
                placeholder="Escriba un comentario..."
                rows={2}
                className="bg-white text-sm resize-none"
              />
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageSelect}
                  className="hidden"
                  id="pqr-comment-image-upload"
                  disabled={loading || uploadingImages}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => document.getElementById('pqr-comment-image-upload').click()}
                  disabled={loading || uploadingImages}
                  className="h-8 text-xs"
                >
                  <Upload className="h-3.5 w-3.5 mr-1.5" />
                  Adjuntar
                </Button>
                <input
                  type="text"
                  value={accion}
                  onChange={(e) => setAccion(e.target.value)}
                  placeholder="Acción realizada (opcional)"
                  className="flex-1 h-8 rounded-md border border-input bg-white px-2.5 text-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
                <Button
                  onClick={handleSubmit}
                  disabled={loading || uploadingImages}
                  size="sm"
                  className="h-8 px-4"
                >
                  {uploadingImages ? 'Subiendo...' : loading ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>

              {selectedImages.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {selectedImages.map((file, index) => (
                    <div key={index} className="flex items-center gap-1 px-2 py-1 bg-white border rounded text-xs">
                      <ImageIcon className="h-3 w-3 text-muted-foreground" />
                      <span className="truncate max-w-[100px]">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(index)}
                        disabled={loading || uploadingImages}
                        className="ml-0.5 text-muted-foreground hover:text-red-500"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PqrManageModal;
